import { TimerObservable } from 'rxjs/observable/TimerObservable';
import { Component, Input, OnInit, ViewChild, ElementRef, EventEmitter, Output, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

declare function require(name: string);
const flatpickr = require('flatpickr');
const rangePlugin = require('flatpickr/dist/plugins/rangePlugin');
const confirmDatePlugin = require('flatpickr/dist/plugins/confirmDate/confirmDate');


@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatePickerComponent implements OnInit {

  @Input()
  public startFormControl: FormControl;

  @Input()
  public endFormControl: FormControl;

  @Output()
  public modelChange: EventEmitter<any> = new EventEmitter<any>();

  private startDateFlatPickerInstance: any;

  private endDateFlatPickerInstance: any;

  private globalMinDate = moment().add(-60, 'day');
  private globalTodayDate = moment();

  constructor() {
  }

  ngOnInit() {
    if (!this.startFormControl) {
      this.startFormControl = new FormControl(null);
    }

    if (!this.endFormControl) {
      this.endFormControl = new FormControl(null);
    }

    const datePickerComponent = this;
    const defaultStartTime = this.startFormControl.value ? moment(this.startFormControl.value).format('YYYY-MM-DD HH:mm:ss') : null;
    const defaultEndTime = this.endFormControl.value ? moment(this.endFormControl.value).format('YYYY-MM-DD HH:mm:ss') : null;

    this.startDateFlatPickerInstance = flatpickr('input[data-input-type=start].date-field-width', {
      enableTime: true,
      enableSeconds: true,
      minuteIncrement: 2,
      onOpen: [
        (selectedDates /*start date*/, dateStr, instance) => {
          const globalMinDateStr = this.globalMinDate.format('YYYY-MM-DD');
          const globalTodayDateStr = this.globalTodayDate.format('YYYY-MM-DD');
          
          if(!this.startFormControl.value && !this.endFormControl.value) {
            // no start date or end date selected yet
            this.startDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.startDateFlatPickerInstance.set('maxDate', globalTodayDateStr);
            
            this.startDateFlatPickerInstance.set('minTime', '00:00:00');
            this.startDateFlatPickerInstance.set('maxTime', '23:59:59');
          } else if(!this.startFormControl.value && this.endFormControl.value) {
            // end date selected and no start date
            this.startDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.startDateFlatPickerInstance.set('maxDate', moment(this.endFormControl.value).format('YYYY-MM-DD'));

            this.startDateFlatPickerInstance.set('minTime', '00:00:00');
            this.startDateFlatPickerInstance.set('maxTime', '23:59:59');
          } else if(this.startFormControl.value && this.endFormControl.value) {
            this.startDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.startDateFlatPickerInstance.set('maxDate', moment(this.endFormControl.value).format('YYYY-MM-DD'));
            
            this.startDateFlatPickerInstance.set('minTime', '00:00:00');
            this.startDateFlatPickerInstance.set('maxTime', '23:59:59');
          }  else if(this.startFormControl.value && !this.endFormControl.value) {
            this.startDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.startDateFlatPickerInstance.set('maxDate', globalTodayDateStr);
            
            this.startDateFlatPickerInstance.set('minTime', '00:00:00');
            this.startDateFlatPickerInstance.set('maxTime', '23:59:59');
          }
        }
      ],
      onValueUpdate: (date) => {
        if (date[0]) {
          datePickerComponent.startFormControl.setValue(moment(date[0]).toDate());
          // For setting max and min time
          const startDateStr = moment(date[0]).format('YYYY-MM-DD');
          this.startDateFlatPickerInstance.set('minTime', '00:00:00');
          this.startDateFlatPickerInstance.set('maxTime', '23:59:59');
          if (this.endFormControl.value) {
            const endDateStr = moment(this.endFormControl.value).format('YYYY-MM-DD');
            const maxStartTimeStr = moment(this.endFormControl.value).format('HH:mm:ss');
            if (endDateStr === startDateStr) {
              this.startDateFlatPickerInstance.set('maxTime', maxStartTimeStr);
              this.startDateFlatPickerInstance.set('defaultHour', maxStartTimeStr.split(':')[0]);
              this.startDateFlatPickerInstance.set('defaultMinute', maxStartTimeStr.split(':')[1]);
            } else {
              this.startDateFlatPickerInstance.set('defaultHour', 12);
              this.startDateFlatPickerInstance.set('defaultMinute', 0);
            }
          }
        }
      },
      onClose() {
        const timer: Subscription = TimerObservable.create(0, 10).subscribe(
          () => {
            datePickerComponent.onChange();
            timer.unsubscribe();
          }
        );
      },
      onReady() { this.showTimeInput = true },
      defaultDate: [defaultStartTime],
      "plugins": [new confirmDatePlugin({showAlways: true})]
    });

    this.endDateFlatPickerInstance = flatpickr('input[data-input-type=end].date-field-width', {
      enableTime: true,
      enableSeconds: true,
      minuteIncrement: 2,
      onOpen: [
        (selectedDates /*end date */, dateStr, instance) => {
          const globalMinDateStr = this.globalMinDate.format('YYYY-MM-DD');
          const globalTodayDateStr = this.globalTodayDate.format('YYYY-MM-DD');

          if(!this.startFormControl.value && !this.endFormControl.value) {
            // no start date or end date selected yet
            this.endDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.endDateFlatPickerInstance.set('maxDate', globalTodayDateStr);
            
            this.endDateFlatPickerInstance.set('minTime', '00:00:00');
            this.endDateFlatPickerInstance.set('maxTime', '23:59:59');
          } else if(this.startFormControl.value && !this.endFormControl.value) {
            // start date selected and no end date
            this.endDateFlatPickerInstance.set('minDate', moment(this.startFormControl.value).format('YYYY-MM-DD'));
            this.endDateFlatPickerInstance.set('maxDate', globalTodayDateStr);

            this.endDateFlatPickerInstance.set('minTime', '00:00:00');
            this.endDateFlatPickerInstance.set('maxTime', '23:59:59');
          } else if(this.startFormControl.value && this.endFormControl.value) {
            this.endDateFlatPickerInstance.set('minDate', moment(this.startFormControl.value).format('YYYY-MM-DD'));
            this.endDateFlatPickerInstance.set('maxDate', globalTodayDateStr);

            this.endDateFlatPickerInstance.set('minTime', '00:00:00');
            this.endDateFlatPickerInstance.set('maxTime', '23:59:59');
          } else if(!this.startFormControl.value && this.endFormControl.value) {
            this.endDateFlatPickerInstance.set('minDate', globalMinDateStr);
            this.endDateFlatPickerInstance.set('maxDate', globalTodayDateStr);

            this.endDateFlatPickerInstance.set('minTime', '00:00:00');
            this.endDateFlatPickerInstance.set('maxTime', '23:59:59');
          }
        }
      ],
      onValueUpdate: (date) => {
        if (date[0]) {
          datePickerComponent.endFormControl.setValue(moment(date[0]).toDate());
          // For setting max and min time
          const endDateStr = moment(date[0]).format('YYYY-MM-DD');
          this.endDateFlatPickerInstance.set('minTime', '00:00:00');
          this.endDateFlatPickerInstance.set('maxTime', '23:59:59');
          if (this.startFormControl.value) {
            const startDateStr = moment(this.startFormControl.value).format('YYYY-MM-DD');
            const minEndTimeStr = moment(this.startFormControl.value).format('HH:mm:ss');
            if (endDateStr === startDateStr) {
              this.endDateFlatPickerInstance.set('minTime', minEndTimeStr);
              this.endDateFlatPickerInstance.set('defaultHour', minEndTimeStr.split(':')[0]);
              this.endDateFlatPickerInstance.set('defaultMinute', minEndTimeStr.split(':')[1]);
            } else {
              this.endDateFlatPickerInstance.set('defaultHour', 12);
              this.endDateFlatPickerInstance.set('defaultMinute', 0);
            }
          }
        }
      },
      onClose() {
        const timer: Subscription = TimerObservable.create(0, 10).subscribe(
          () => {
            datePickerComponent.onChange();
            timer.unsubscribe();
          }
        );
      },
      onReady() { this.showTimeInput = true },
      defaultDate: [defaultEndTime],
      "plugins": [new confirmDatePlugin({showAlways: true})]
    });
  }

  public clear() {
    if (this.startDateFlatPickerInstance) {
      this.startDateFlatPickerInstance.clear();
    }

    if (this.endDateFlatPickerInstance) {
      this.endDateFlatPickerInstance.clear();
    }
  }

  private onChange() {
    this.modelChange.emit({
      startDate: this.startFormControl.value,
      endDate: this.endFormControl.value
    });
  }
}
